import Papa from "papaparse";

import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";
import { detectCsvDelimiter } from "../../services/UtilityService";
import {
  PermissionsImportFile,
  PermissionsImportStructuredData,
  PermissionsImportValidatedData,
} from "./PermissionsImport.d";

const parseIds = (value: string): number[] | string => {
  const trimmed = value?.trim().toUpperCase();
  if (trimmed === "ALL" || trimmed === "NONE") return trimmed;
  if (!value || !value.trim()) return [];
  return value
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map(Number);
};

export const fetchRoles = (setRoles: Function, language: string) => {
  ApiService.get("roles")
    .then((response) => setRoles(response.data.roles))
    .catch((err) => {
      console.error("Error fetching roles:", err);
      alert(translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.ERROR.LOAD_ROLES"));
    });
};

export const validateImportedRoles = (
  data: Array<PermissionsImportStructuredData>,
  language: string,
  onSuccess: Function,
  setValidating: Function,
  showAlert: Function,
  popAlert: Function
) => {
  setValidating(true);

  ApiService.post("roles/permissions/batch/check", {
    entries: data.map(({ rowIndex, ...entry }) => entry),
  })
    .then(() => {
      popAlert?.();
      onSuccess(
        data.map((entry, index) => ({
          ...entry,
          rowIndex: index,
        }))
      );
    })
    .catch((err) => {
      popAlert?.();
      showAlert?.({
        type: "danger",
        title: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.VALIDATION.ERROR.TITLE"),
        message: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.VALIDATION.ERROR.MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: err?.data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(err.data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => setValidating(false));
};

export const assignImportedRoles = (
  data: Array<PermissionsImportValidatedData>,
  language: string,
  setSubmitting: Function,
  onSuccess: Function,
  showAlert: Function,
  popAlert: Function
) => {
  setSubmitting(true);

  showAlert?.({
    type: "info",
    title: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.LOADING.TITLE"),
    message: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.LOADING.MESSAGE"),
    clickOutsideToClose: false,
    buttons: [],
  });

  ApiService.post("roles/permissions/batch", {
    entries: data.map(({ rowIndex, ...entry }) => entry),
  })
    .then((_) => {
      popAlert?.();
      onSuccess();
      showAlert?.({
        type: "success",
        title: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.SUCCESS.TITLE"),
        message: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.SUCCESS.MESSAGE"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.OK"),
            onClick: () => popAlert?.(),
          },
        ],
      });
    })
    .catch((err) => {
      popAlert?.();
      showAlert?.({
        type: "danger",
        title: translate(language, "ERROR.API_ERROR"),
        message: translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.ERROR.GENERIC"),
        clickOutsideToClose: true,
        buttons: [
          {
            label: translate(language, "GLOBAL.CLOSE"),
            variant: "danger",
            onClick: () => popAlert?.(),
          },
        ],
        extraContent: err?.data ? (
          <pre className="text-start off-white-background p-2 rounded border">{JSON.stringify(err.data, null, 2)}</pre>
        ) : null,
      });
    })
    .finally(() => setSubmitting(false));
};

export const importCsvFile = (
  file: File,
  language: string,
  setCsvData: Function,
  setValidatedData: Function,
  setValidating: Function,
  showAlert: Function,
  popAlert: Function
) => {
  detectCsvDelimiter(file).then((delimiter) => {
    Papa.parse<PermissionsImportFile>(file, {
      header: true,
      skipEmptyLines: true,
      delimiter: delimiter,
      complete: (results: Papa.ParseResult<PermissionsImportFile>) => {
        if (results.errors.length > 0) {
          console.error("CSV Parse Errors:", results.errors);
          alert(translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.ERROR.CSV_PARSE"));
          return;
        }

        const transformedData: PermissionsImportStructuredData[] = results.data.map(
          (row: PermissionsImportFile, index: number) => ({
            email: row.Email?.trim(),
            role_id: Number(row["Role Id"]?.trim()),
            users_ids_scope: parseIds(row["Users Ids scope"]),
            organizers_ids_scope: parseIds(row["Organizers Ids scope"]),
            cohorts_ids_scope: parseIds(row["Cohorts Ids scope"]),
            animators_ids_scope: parseIds(row["Animators Ids scope"]),
            projects_ids_scope: parseIds(row["Project Ids scope"]),
            rowIndex: index,
          })
        );

        validateImportedRoles(
          transformedData,
          language,
          (validatedData: PermissionsImportValidatedData[]) => {
            setCsvData(transformedData);
            setValidatedData(validatedData);
          },
          setValidating,
          showAlert,
          popAlert
        );
      },
      error: (error: Error) => {
        console.error("Error parsing CSV:", error);
        alert(`${translate(language, "PAGES.PERMISSIONS_IMPORT.ALERT.IMPORT.ERROR.CSV_PARSE")} ${error.message}`);
      },
    });
  });
};

export const processFileInput = (
  event: React.ChangeEvent<HTMLInputElement>,
  importCsvFileCallback: (file: File) => void
) => {
  const file = event.target.files?.[0];
  if (file) {
    importCsvFileCallback(file);
    event.target.value = "";
  }
};
